import React, { useMemo } from 'react';
import { Skeleton, Select, MenuItem, Stack } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { minimalInputTheme } from '@/app/theme';
import { usePipelineContext } from '@/context/pipelineContext';
import {
	useUpdateClientRowMutation,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';

export const ProspectStageSelect = ({ minimal = true }) => {
	const dispatch = useDispatch();
	const { clientRow, insight, clientId } = useObjectContext();

	const { stages: pipelineStages, isLoading: isPipelineLoading } = usePipelineContext(
		process.env.REACT_APP_PIPELINE_PROSPECTS,
	);
	const [updateClientx] = useUpdateClientxMutation();

	const [updateClientRow, { isLoading: isUpdating }] = useUpdateClientRowMutation();

	const currentStage = useMemo(
		() =>
			(pipelineStages ?? []).find(
				(s) => s.id === clientRow?.stage || s.stageId === clientRow?.stage,
			),
		[pipelineStages, clientRow?.stage],
	);

	const handleStageChange = async (event) => {
		const selectedStageId = event.target.value;
		const selectedStageMeta = (pipelineStages ?? []).find(
			(s) => s.id === selectedStageId || s.stageId === selectedStageId,
		);
		const isClosed = Boolean(selectedStageMeta?.closed);

		if (isClosed) {
			dispatch(
				openDialog({
					dialogName: 'lossReason',
					data: {
						clientId,
						docId: clientRow?.docId,
						stageId: selectedStageId,
						insightId: insight?.Id,
					},
				}),
			);
			return;
		}

		try {
			// Update insight client status if closed/active...  0 - Active, 1 - Inactive
			if (insight?.CurrentStatusId === 1) {
				await updateClientx({
					clientId,
					insight: { CurrentStatusId: 0 },
					firebase: { stage: selectedStageId },
				}).unwrap();
			} else {
				await updateClientRow({
					clientId,
					docId: clientRow?.docId,
					properties: { stage: selectedStageId },
				}).unwrap();
			}
		} catch (error) {
			console.error('Update stage failed:', error);
		}
	};

	const isDisabled = (pipelineStages?.length ?? 0) === 0 || isPipelineLoading || isUpdating;

	return (
		<Stack width='100%'>
			{isPipelineLoading ? (
				<Skeleton animation='wave' width='100%' height='2em' />
			) : (
				<Select
					data-testid='prospect-stage-select'
					fullWidth
					value={currentStage?.id ?? currentStage?.stageId ?? ''}
					onChange={handleStageChange}
					disabled={isDisabled}
					variant={minimal ? 'standard' : 'outlined'}
					sx={{
						...(minimal && {
							...minimalInputTheme,
							'& .Mui-disabled': {
								backgroundColor: 'transparent',
							},
						}),
					}}
				>
					{(pipelineStages ?? []).map((stage) => (
						<MenuItem
							key={stage?.stageId ?? stage?.id}
							value={stage?.id ?? stage?.stageId}
							aria-label={`Stage ${stage?.name ?? stage?.label}`}
						>
							{stage?.name ?? stage?.label ?? stage.id}
						</MenuItem>
					))}
				</Select>
			)}
		</Stack>
	);
};
